import React from 'react'
import { useForm, Link, usePage } from '@inertiajs/react'
import { Box, Paper, Typography, TextField, Button, Stack, Alert } from '@mui/material'

interface Props {
  email?: string
  errors?: Record<string, string[]>
}

interface SharedProps {
  flash?: { notice?: string | null; alert?: string | null }
}

function SignIn({ email = '', errors: serverErrors = {} }: Props) {
  const page = usePage<SharedProps>()
  const flash = page.props.flash || {}

  const { data, setData, post, processing } = useForm({
    user: {
      email,
      password: '',
      remember_me: false,
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post('/users/sign_in')
  }

  const baseError = serverErrors.base?.[0] || flash.alert

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 120px)', p: 2 }}>
      <Paper sx={{ p: 4, maxWidth: 420, width: '100%' }}>
        <Typography variant="h4" sx={{ mb: 1, textAlign: 'center' }}>Welcome back</Typography>
        <Typography variant="body2" sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
          Sign in to continue your K-pop journey.
        </Typography>

        {baseError && <Alert severity="error" sx={{ mb: 2 }}>{baseError}</Alert>}
        {flash.notice && <Alert severity="success" sx={{ mb: 2 }}>{flash.notice}</Alert>}

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              label="Email"
              type="email"
              value={data.user.email}
              onChange={(e) => setData('user', { ...data.user, email: e.target.value })}
              error={Boolean(serverErrors.email)}
              helperText={serverErrors.email?.join(', ')}
              required
              autoComplete="email"
              fullWidth
            />
            <TextField
              label="Password"
              type="password"
              value={data.user.password}
              onChange={(e) => setData('user', { ...data.user, password: e.target.value })}
              error={Boolean(serverErrors.password)}
              helperText={serverErrors.password?.join(', ')}
              required
              autoComplete="current-password"
              fullWidth
            />
            <Button type="submit" variant="contained" size="large" disabled={processing}>
              Sign in
            </Button>
          </Stack>
        </form>

        <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
          <Link href="/users/password/new">Forgot password?</Link>
          <Link href="/users/sign_up">Create an account</Link>
        </Stack>
      </Paper>
    </Box>
  )
}

;(SignIn as React.ComponentType & { noLayout?: boolean }).noLayout = true

export default SignIn
